package com.bikompanion.ui.mapper

import com.bikompanion.domain.BikeParkingSpot
import com.bikompanion.domain.ParkingCell
import com.bikompanion.ui.models.ParkingAreaSummary
import kotlin.math.floor
import kotlin.math.pow
import kotlin.math.round

/**
 * Roughly how far apart cluster markers should sit on screen, in pixels.
 *
 * Chosen to be a little wider than the markers themselves so they read as separate counts rather
 * than a chain. Everything else about the grid follows from this and the zoom.
 */
private const val ClusterSpacingPixels = 80.0

/** Web Mercator's tile size, which is what ties a zoom level to a number of pixels per degree. */
private const val TilePixels = 256.0

/**
 * Groups racks into the counted blobs the map draws when it is too far out to show them singly.
 *
 * Racks are dense — a city block can carry a dozen, and one fetched cell up to ten thousand — so
 * below `PARKING_SPOTS_ZOOM` drawing them individually gave a smear of overlapping dots that told
 * a rider nothing, and cost a projection through the map per rack per frame. Clustering answers
 * the question that zoom actually asks ("is there anywhere to lock up over there?") with far
 * fewer marks.
 *
 * Done here rather than in the draw phase because it is O(racks) and the draw phase runs on every
 * frame of a drag. The grid only changes on whole zoom levels, so a pan recomputes nothing.
 */
fun clusterParkingSpots(
    racksByCell: Map<ParkingCell, List<BikeParkingSpot>>,
    zoom: Double,
): List<ParkingAreaSummary> {
    val grid = gridDegreesAt(zoom)
    val counts = HashMap<Long, ClusterBuilder>()
    racksByCell.values.forEach { racks ->
        racks.forEach { rack ->
            val row = floor(rack.latitude / grid).toLong()
            val column = floor(rack.longitude / grid).toLong()
            // Longitude spans 360/grid columns; the shift is comfortably clear of that at
            // every zoom this is used at, so no two cells share a key.
            counts.getOrPut((row shl 32) xor (column and 0xFFFFFFFFL)) { ClusterBuilder() }.add(rack)
        }
    }
    return counts.values.map(ClusterBuilder::build)
}

/**
 * The side of a cluster cell in degrees, so that neighbouring markers land about
 * [ClusterSpacingPixels] apart at this zoom.
 *
 * Snapped to whole zoom levels: a continuous grid would re-cluster on every pinch frame, and
 * markers would slide against the map as their buckets shifted underneath them.
 */
private fun gridDegreesAt(zoom: Double): Double {
    val whole = floor(zoom).coerceIn(0.0, 22.0)
    return ClusterSpacingPixels * 360.0 / (TilePixels * 2.0.pow(whole))
}

/** Accumulates a bucket's racks into a count and a middle, without holding on to any of them. */
private class ClusterBuilder {
    private var count = 0
    private var latitudeSum = 0.0
    private var longitudeSum = 0.0

    fun add(rack: BikeParkingSpot) {
        count++
        latitudeSum += rack.latitude
        longitudeSum += rack.longitude
    }

    /**
     * The marker sits at the middle of the racks it stands for, not the middle of its grid
     * cell: a bucket whose racks are all along one street should point at that street.
     *
     * Rounded so a bucket that comes back with identical racks compares equal and invalidates
     * nothing downstream — the summaries are held with `structuralEqualityPolicy`.
     */
    fun build() =
        ParkingAreaSummary(
            latitude = round(latitudeSum / count * 1_000_000) / 1_000_000,
            longitude = round(longitudeSum / count * 1_000_000) / 1_000_000,
            spotCount = count,
        )
}
